import {
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  InventoryResource,
  InventoryVolume,
  Snapshot,
} from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { Tag } from '@/components/tag';
import { volumePresets } from '@/components/volumes/volumeSettings';
import { useViewParameters } from '@/contexts/navigation';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { isDateValid } from '@/utils/dateTime';
import { DATE_FORMAT, dayjs } from '@/utils/dayjs';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import { KmsKeyRow } from './kmsKeyRow';
import { LatestSnapshotIndicator } from './latestSnapshotIndicator';

import { Panels } from '../../../panels';

function getVolumeBackupStatusText(volume: InventoryVolume): string {
  if (volume.excludedFromBackupAt) return 'Excluded from future backups';
  if (volume.isRoot) return 'Root volume is always included in backups';
  return 'Included in future backups';
}

function getExcludeDisabledReason(
  volume: InventoryVolume,
  resource: InventoryResource,
  pendingVolumeIds: Set<string>,
  isAuthorizedResource: ReturnType<typeof useRoles>['isAuthorizedResource']
): string | undefined {
  if (volume.isRoot) return 'Root volumes cannot be excluded from backup';
  if (!isAuthorizedResource('update:exclude_resource', resource))
    return 'You do not have permission to exclude volumes';
  if (pendingVolumeIds.has(volume.providerVolumeId))
    return 'Operation in progress';
  return undefined;
}

export const AwsVolumesSection = ({
  resource,
  onEntityChange,
}: {
  resource: InventoryResource;
  onEntityChange: () => Promise<void>;
}) => {
  const { rightPanel, snackbar } = useWorkspace();
  const { isAuthorizedResource } = useRoles();
  const { Ec2VolumeBackupExclusionEnabled } = useFeatureFlags();
  const dal = useDAL();
  const { removeParam } = useViewParameters();
  const [pendingVolumeIds, setPendingVolumeIds] = useState<Set<string>>(
    new Set()
  );
  const { data: body, isLoading: isLoadingSnapshots } =
    dal.inventory.snapshots.useList(resource.id, 0, 100);
  const { vaults } = useBackupVaults();

  const volumeToLatestSnapshotMapping = useMemo(
    () =>
      (body?.data || []).reduce<Record<string, Snapshot>>((agg, snapshot) => {
        if (isDateValid(snapshot.pointInTime)) {
          snapshot.resource?.properties?.awsEc2?.volumes?.forEach((volume) => {
            if (!agg[volume.providerVolumeId]) {
              agg[volume.providerVolumeId] = snapshot;
            } else if (
              snapshot.pointInTime!.getTime() >
              agg[volume.providerVolumeId].pointInTime!.getTime()
            ) {
              agg[volume.providerVolumeId] = snapshot;
            }
          });
        }

        return agg;
      }, {}),
    [body?.data]
  );

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'overview',
      },
    });
  };

  const toggleVolumeBackupExclusion = async (
    volumeId: string,
    excludedFromBackup: boolean
  ) => {
    setPendingVolumeIds((prev) => new Set(prev).add(volumeId));
    try {
      if (excludedFromBackup) {
        await dal.inventory.resources.cancelVolumeBackupExclusion(
          resource.id,
          volumeId
        );
      } else {
        await dal.inventory.resources.excludeVolumeFromBackup(
          resource.id,
          volumeId
        );
      }
      await onEntityChange();
    } catch {
      snackbar.showErrorMessage(
        excludedFromBackup
          ? 'Failed to include volume in backup.'
          : 'Failed to exclude volume from backup.'
      );
    } finally {
      setPendingVolumeIds((prev) => {
        const next = new Set(prev);
        next.delete(volumeId);
        return next;
      });
    }
  };

  return (
    <Stack gap='12px' divider={<Divider />}>
      {resource.resourceProperties?.awsEc2?.volumes?.map((volume) => {
        const snapshot = volumeToLatestSnapshotMapping[volume.providerVolumeId];
        const vault = vaults.find((v) => v.id === snapshot?.vaultId);
        const excludeDisabledReason = getExcludeDisabledReason(
          volume,
          resource,
          pendingVolumeIds,
          isAuthorizedResource
        );
        const excludeActionDisabled = Boolean(excludeDisabledReason);

        return (
          <Accordion key={volume.providerVolumeId} className='px-[40px]'>
            <AccordionSummary
              sx={{
                '.Mui-expanded': {
                  '.snapshot-info': {
                    opacity: '0',
                  },
                },
              }}
            >
              <Stack
                className='w-full'
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Stack direction='row' alignItems='center' gap='8px'>
                  <Typography variant='h4'>{volume.path}</Typography>
                  {volume.isRoot && <Tag content='Root' variant='secondary' />}
                </Stack>
                {vault && (
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    className='snapshot-info'
                    sx={{
                      transition: 'linear 0.1s',
                    }}
                  >
                    <SnapshotIndicator
                      backgroundColor={vault.backgroundColor!}
                    />
                    <Typography
                      className='ml-[12px]'
                      sx={{ fontWeight: '400 !important' }}
                    >
                      {dayjs.utc(snapshot.pointInTime).format(DATE_FORMAT)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails className='py-[12px] pl-[36px] pr-0 overflow-hidden'>
              {(vault || Ec2VolumeBackupExclusionEnabled) && (
                <Stack
                  className='w-full'
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  {vault ? (
                    <LatestSnapshotIndicator
                      latestSnapshot={snapshot}
                      isLoading={isLoadingSnapshots}
                    />
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      {getVolumeBackupStatusText(volume)}
                    </Typography>
                  )}
                  <Stack direction='row' gap='12px' className='my-[16px]'>
                    {Ec2VolumeBackupExclusionEnabled && (
                      <Tooltip title={excludeDisabledReason} arrow>
                        <span>
                          <Button
                            disabled={excludeActionDisabled}
                            variant='outlined'
                            color={
                              volume.excludedFromBackupAt
                                ? 'success'
                                : 'inherit'
                            }
                            onClick={() =>
                              void toggleVolumeBackupExclusion(
                                volume.providerVolumeId,
                                Boolean(volume.excludedFromBackupAt)
                              )
                            }
                          >
                            {volume.excludedFromBackupAt
                              ? 'Include'
                              : 'Exclude'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {vault && (
                      <Button
                        disabled={
                          !isAuthorizedResource(
                            'create:restore_resource',
                            resource
                          )
                        }
                        variant='outlined'
                        onClick={() =>
                          rightPanel.setComponent({
                            panel: Panels.RestoreVolumeWizard,
                            props: {
                              resource,
                              providerVolumeId: volume.providerVolumeId,
                              snapshot,
                              actionType: 'volume',
                              onClose,
                              onBack,
                            },
                          })
                        }
                      >
                        <i
                          className={`material-symbols-settings-backup-restore-rounded text-[18px] mr-[8px] text-(--mui-palette-text-primary) align-middle`}
                        />
                        Restore
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
              {Ec2VolumeBackupExclusionEnabled &&
                !vault &&
                volume.excludedFromBackupAt && (
                  <Typography variant='body2' color='text.secondary'>
                    This volume will not appear in future restore options until
                    it is included again.
                  </Typography>
                )}
              <Stack direction={'row'} gap={'60px'}>
                <Table
                  sx={{
                    tableLayout: 'fixed',
                    td: {
                      padding: '4px 0',
                      borderBottom: 'none',
                      lineHeight: '24px',
                      height: '24px',
                    },

                    '& .props-col': {
                      whiteSpace: 'nowrap',
                      fontWeight: '400',
                      width: '120px',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell className='props-col'>ID</TableCell>
                      <TableCell>
                        <CopyToClipboardWrapper
                          textToCopy={volume.providerVolumeId}
                        >
                          <Typography>{volume.providerVolumeId}</Typography>
                        </CopyToClipboardWrapper>
                      </TableCell>
                    </TableRow>
                    <KmsKeyRow
                      kmsKeyId={volume.encryptionKeyId}
                      isAwsManagedKey={volume.isAwsManagedKey}
                    />
                    <TableRow>
                      <TableCell className='props-col'>Type</TableCell>
                      <TableCell>
                        {volumePresets[volume.volumeSettings.type]?.displayName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Size</TableCell>
                      <TableCell>
                        {fileSizeFormatter(
                          volume.volumeSettings.sizeBytes,
                          1,
                          false
                        )}
                      </TableCell>
                    </TableRow>
                    {volume.volumeSettings.iops && (
                      <TableRow>
                        <TableCell className='props-col'>IOPS</TableCell>
                        <TableCell>{volume.volumeSettings.iops}</TableCell>
                      </TableRow>
                    )}
                    {volume.volumeSettings.throughput && (
                      <TableRow>
                        <TableCell className='props-col'>Throughput</TableCell>
                        <TableCell>{`${volume.volumeSettings.throughput} MB/s`}</TableCell>
                      </TableRow>
                    )}
                    {!!Object.entries(volume.tags || {}).length && (
                      <TableRow>
                        <TableCell className='props-col'>Tags</TableCell>
                        <TableCell>
                          <Stack
                            width='80%'
                            direction={'row'}
                            flexWrap={'wrap'}
                            gap={'8px'}
                          >
                            {Object.entries(volume.tags || {}).map((entry) => (
                              <Tag
                                variant='secondary'
                                key={entry[0]}
                                content={entry.filter((x) => x).join('=')}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};
